import type { Defect, Scenario, Severity, Target } from './types';
import { targetKey } from './types';

/**
 * Severity weighting. A player who finds the sprint-killing problem and misses
 * two cosmetic ones should outscore the reverse.
 */
export const SEVERITY_WEIGHT: Record<Severity, number> = {
  critical: 3,
  major: 2,
  minor: 1,
};

/**
 * Cost of flagging something that is actually fine.
 *
 * ONE WRONG ACCUSATION CANCELS ONE CORRECT FIND. The penalty is the average
 * value of a real defect on that level — 100 divided by however many there are
 * — rather than a flat number.
 *
 * A flat 5 did not scale. On a level with five real defects and two decoys,
 * flagging every arrow found everything and cost 10, scoring 90: the
 * false-positive penalty was almost free, so the scattergun was a winning
 * strategy on exactly the levels meant to punish it. Tying the penalty to what
 * a find is worth makes "flag everything" cost as much as it gains, on every
 * level, whatever the ratio.
 *
 * It is also a rule a player can hold in their head, which a sliding scale or
 * an escalating series would not be.
 */
export function falsePositivePenalty(realDefectCount: number): number {
  // A level with no real defects is refused by validation; guard anyway rather
  // than divide by zero and hand out a NaN score.
  if (realDefectCount <= 0) return 100;
  return 100 / realDefectCount;
}

/**
 * Kept for display only, where a level is not in scope.
 *
 * @deprecated Use falsePositivePenalty(level.defects.length) — the real cost
 * depends on the level.
 */
export const FALSE_POSITIVE_PENALTY = 5;

export interface Grade {
  found: Defect[];
  missed: Defect[];
  /** Target keys the player flagged that carry no defect. */
  falsePositives: string[];
  /** 0-100, penalties applied, floored at zero. */
  score: number;
  rating: string;
}

/** Every target that counts as spotting this defect. */
function acceptedTargets(defect: Defect): Target[] {
  return defect.altTargets ? [defect.target, ...defect.altTargets] : [defect.target];
}

export function grade(scenario: Scenario, flagged: ReadonlySet<string>): Grade {
  // Any accepted target of any defect is "legitimate to flag", so alternates
  // never register as false positives.
  const legitimate = new Set<string>();
  for (const defect of scenario.defects) {
    for (const target of acceptedTargets(defect)) {
      legitimate.add(targetKey(target));
    }
  }

  const found: Defect[] = [];
  const missed: Defect[] = [];
  for (const defect of scenario.defects) {
    const hit = acceptedTargets(defect).some((t) => flagged.has(targetKey(t)));
    (hit ? found : missed).push(defect);
  }

  const falsePositives = [...flagged].filter((key) => !legitimate.has(key));

  const totalWeight = scenario.defects.reduce(
    (sum, d) => sum + SEVERITY_WEIGHT[d.severity],
    0,
  );
  const earnedWeight = found.reduce((sum, d) => sum + SEVERITY_WEIGHT[d.severity], 0);

  const raw =
    totalWeight === 0
      ? 0
      : (earnedWeight / totalWeight) * 100 -
        falsePositives.length * falsePositivePenalty(scenario.defects.length);

  const score = Math.max(0, Math.round(raw));

  return { found, missed, falsePositives, score, rating: rate(score) };
}

function rate(score: number): string {
  if (score >= 90) return 'Board Auditor';
  if (score >= 70) return 'Seasoned Scrum Master';
  if (score >= 50) return 'Promising Junior';
  if (score >= 25) return 'Needs a Retro';
  return 'Ship It Anyway';
}

/** Total number of planted defects, for the "x of y" counters. */
export function defectCount(scenario: Scenario): number {
  return scenario.defects.length;
}
